import { format, formatISO, startOfWeek as dateFnsStartOfWeek } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { opacities } from "./hex_opacity";
import sessionHelperInstance from "services/sessionHelper";

const days = ["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"];

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toIST = (date: any) => {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return formatInTimeZone(
    dateObj,
    "Asia/Kolkata",
    "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  );
};

const formatUtcDate = (date: any, formatStr: any) => {
  const _format = formatStr ?? "dd-MM-yyyy HH:mm:ss";
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return format(dateObj, _format);
};

const formatDate = (date, formatStr = "dd - MM - yyyy") => {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  if (formatStr === "utc") return formatISO(dateObj);

  return format(dateObj, formatStr);
};

const formatTime = (date, formatStr = "hh:mm") => {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  if (formatStr === "utc") return formatISO(dateObj);

  return format(dateObj, formatStr);
};

const startOfWeek = () => {
  return dateFnsStartOfWeek(new Date());
};

const startOfMonth = () => {
  let date = new Date();

  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const sortArray = (filteredlist, orderBy, column) => {
  const key = column?.id || "";
  if (!key) return filteredlist;

  if (orderBy === "asc") {
    switch (column?.type) {
      case "number":
        return filteredlist.sort((a, b) => a[key] - b[key]);
      case "string":
        return filteredlist.sort((a, b) =>
          a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0,
        );
      case "date":
        return filteredlist.sort(function (a, b) {
          return (
            new Date(b[key.replace("Text", "")]).getTime() -
            new Date(a[key.replace("Text", "")]).getTime()
          );
        });
      default:
        return filteredlist;
    }
  } else {
    switch (column?.type) {
      case "string":
        return filteredlist.sort((a, b) =>
          a[key] < b[key] ? 1 : b[key] < a[key] ? -1 : 0,
        );
      case "number":
        return filteredlist.sort((a, b) => b[key] - a[key]);
      case "date":
        return filteredlist
          .sort(function (a, b) {
            return (
              new Date(b[key.replace("Text", "")]).getTime() -
              new Date(a[key.replace("Text", "")]).getTime()
            );
          })
          .reverse();
      default:
        return filteredlist;
    }
  }
};

const JSONSearch = (list = [], keys = [], value) => {
  const searchValue = value.toLowerCase();
  return list.filter((data) =>
    Object.keys(data).some(
      (key) =>
        keys.includes(key) &&
        data[key] &&
        data[key].toString().toLowerCase().includes(searchValue),
    ),
  );
};

const setNumberFormat = (number: any, decimal: any) => {
  if (!number || isNaN(number) || isNaN(decimal) || decimal < 0) {
    return number?.toString()?.split(".")[0] + "." + `000000`.slice(0, decimal);
  }

  let numString = number?.toString();
  let parts = numString?.split(".");

  if (parts?.length > 1) {
    numString = parts[0] + "." + `${parts[1]}000000`.slice(0, decimal);
  } else numString = Number(parts[0]).toFixed(decimal);

  return numString;
};

const randomNumber = (min: any, max: any) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

const strToSankeCase = (str: any) => {
  if (!str) return "";

  return str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map((x) => x.toLowerCase())
    .join("_");
};

const getFilteredPayload = ({ initial = {}, curr = {} }) =>
  Object.keys(initial).reduce((acc, key) => {
    if (curr.hasOwnProperty(key)) {
      acc[key] = curr[key];
    }
    return acc;
  }, {});

const areArraysEqual = (array1 = [], array2 = []) => {
  if (array1.length !== array2.length) {
    return false;
  }

  const frequencyCounter = {};

  for (let element of array1) {
    frequencyCounter[element] = (frequencyCounter[element] || 0) + 1;
  }

  for (let element of array2) {
    if (!frequencyCounter[element]) {
      return false;
    }
    frequencyCounter[element]--;
  }

  return true;
};

const commaSeparated = (num: any) =>
  num?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const convertToNumber = (num = "0", isCommaSep = false) => {
  return isCommaSep ? Number(num?.toString().replace(/,/g, "")) : Number(num);
};

const convertToMillions = (number: any) => {
  if (Math.abs(number) >= 1e4) {
    return (number / 1e6).toFixed(2) + "M";
  }
  return setNumberFormat(number, 2);
};

function getCurrencySymbol(currencyCode, locale = "en-US") {
  if (["XPS", "xps"].includes(currencyCode)) return currencyCode;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .find((part) => part.type === "currency").value;
}

function sendMessageToCanvas(loadedIframe: any, data: any) {
  if (loadedIframe && loadedIframe?.contentWindow) {
    loadedIframe?.contentWindow?.gameExtension(data, "*");
  }
}

function opacity2hex(alpha: any) {
  let hex = opacities[`${alpha}`];

  if (!hex) {
    return "FF";
  }

  return hex;
}

function capitalize(string: any) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

/**
 * Round a value towards the nearest integer
 * @param value
 * @returns
 */
const roundOff = (value: any) => {
  return Math.trunc(Math.round(value));
};

const delay = (delayInms: any) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

const deepCopy = (data: any) => JSON.parse(JSON.stringify(data));

function blobToBinary(blob: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function () {
      const arrayBuffer = reader.result;
      resolve(arrayBuffer);
    };

    reader.onerror = function (error) {
      reject(error);
    };

    reader.readAsArrayBuffer(blob);
  });
}

async function getVersion() {
  try {
    // Get the current path from the window location
    const currentPath = window.location.pathname
      .split("/")
      .filter(Boolean)
      .pop();

    // Construct the correct path to the version.txt based on the current path (version folder)
    const versionUrl = currentPath
      ? `/${currentPath}/version.txt`
      : `/version.txt`;

    const response = await fetch(versionUrl);

    // Check if the response is okay and ensure it's not the `index.html`
    if (
      !response.ok ||
      !response.headers.get("content-type")?.includes("text/plain")
    ) {
      throw new Error("version.txt not found or is incorrect");
    }

    const version = await response.text();
    return version.trim();
  } catch (error) {
    console.log("Error loading version:", error);
    return "local"; // or another default string
  }
}

const wrapWithAll = (array = []) => [{ id: "all", label: "All" }, ...array];

function generateOptions({ items, id, label, addAllOption = false }) {
  if (!items) return [];

  let returnData = items.map((item) => {
    if (!id) {
      return {
        id: item,
        label: item,
      };
    }
    return {
      id: item?.[id] ?? item,
      label: item?.[label ?? id] ?? item,
    };
  });

  if (addAllOption) returnData.unshift({ id: "all", label: "All" });

  return returnData;
}

const sanitizeParams = (body = {}) => {
  const newBody = {};
  for (let key in body) {
    if (body[key] || [0, false]?.includes(body[key])) {
      newBody[key] = body[key];
    }
  }

  return newBody;
};

function getCurrencyBasedData(data: any, currency: any) {
  return data.map((ds) => {
    let bet = ds?.totalBetConverted[currency] ?? ds?.totalBet;
    let win = ds?.totalWinConverted[currency] ?? ds?.totalWin;
    let rtp = ds?.rtpByCurrency?.[currency] ?? ds?.rtp;
    let ggr = bet - win;
    const _el = {
      ...ds,
      totalBet: ds.totalBetConverted[currency] ?? ds?.totalBet,
      totalWin: ds.totalWinConverted[currency] ?? ds?.totalWin,
      purchaseTotalBet:
        ds.purchaseTotalBetConverted[currency] ?? ds?.purchaseTotalBet,
      grossGamingRevenue: ggr?.toFixed(4) || ds.grossGamingRevenue,
      rtp,
    };
    return _el;
  });
}

function getCurrencyBasedMarketData(data: any, currency: any) {
  return data.map((ds) => {
    let bet = ds?.totalBetConverted[currency] ?? ds?.totalBet;
    let win = ds?.totalWinConverted[currency] ?? ds?.totalWin;
    let ggr = bet - win;
    const _el = {
      ...ds,
      totalBet: ds.totalBetConverted[currency] ?? ds?.totalBet,
      totalWin: ds.totalWinConverted[currency] ?? ds?.totalWin,
      grossGamingRevenue: ggr?.toFixed(4) || ds.grossGamingRevenue,
    };
    return _el;
  });
}

export const validateEmail = (emails: any) => {
  return emails
    .split(",")
    .map((email) => email.trim())
    .every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

export const getUniquesArrayObject = (array, key = "id") => {
  return Array.from(new Map(array.map((item) => [item[key], item])).values());
};

const getCountryCodeByName = (countryName: any) => {
  let val = countryName;
  sessionHelperInstance.countries.forEach((country) => {
    if (country.name === countryName) {
      val = country.isoCode;
      return country.isoCode;
    }
  });

  return val;
};

// Helper to get user's timezone
const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Helper to serialize date picker values
const serializeDateValue = (value: any) => {
  if (!value) return null;

  // Handle Dayjs object (from MUI DatePicker with AdapterDayjs)
  if (value && typeof value === "object" && value.$d) {
    return value.toISOString();
  }

  // Handle Date object
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Handle string
  if (typeof value === "string") {
    return new Date(value).toISOString();
  }

  return value;
};

/**
 * Calculate overall RTP (Return to Player) based on weighted average of market RTP values
 * @param rtpValues - Object containing market RTP values (e.g., { UNDER_OVER: 95, FIRST_LAST_ANY_GOAL_SCORE: 90 })
 * @param disabledMarkets - Market keys that are disabled and should be excluded from the calculation
 * @returns Weighted average RTP as a string with 2 decimal places
 */
const calculateOverallRTP = (
  rtpValues: any,
  disabledMarkets: string[] = [],
): string => {
  // Define weights for specific markets (higher weight = more influence on overall RTP)
  const weights: Record<string, number> = {
    UNDER_OVER: 3,
    FIRST_LAST_ANY_GOAL_SCORE: 3,
  };

  // Keys to exclude from calculation
  const excludedKeys = ["OVER_ALL", "FIRST_GOAL_CORRECT_SCORE"];

  // Filter out excluded keys and disabled markets
  const rtpEntries = Object.entries(rtpValues).filter(
    ([key]) => !excludedKeys.includes(key) && !disabledMarkets.includes(key),
  );

  // Return 0 if no valid entries
  if (rtpEntries.length === 0) return "0.00";

  let total = 0;
  let count = 0;

  // Calculate weighted sum
  for (const [key, value] of rtpEntries) {
    const weight = weights[key] ?? 1; // Default weight is 1 if not specified
    const numValue = parseFloat(String(value)) || 0;
    total += numValue * weight;
    count += weight;
  }

  // Return weighted average with 2 decimal places
  return (total / count).toFixed(2);
};

/**
 * Row-click navigation for list screens. A plain click stays in the current tab; the browser's own
 * new-tab modifiers open a second tab, matching what the anchor on the row would do natively.
 */
const navigateFromRow = (event: any, navigate: any, path: string) => {
  event?.preventDefault?.();

  if (event?.ctrlKey || event?.metaKey || event?.shiftKey) {
    window.open(`#${path}`, "_blank");
    return;
  }

  navigate(path);
};

export {
  deepCopy,
  delay,
  capitalize,
  formatUtcDate,
  formatDate,
  formatTime,
  startOfWeek,
  startOfMonth,
  sortArray,
  JSONSearch,
  setNumberFormat,
  randomNumber,
  strToSankeCase,
  days,
  months,
  getFilteredPayload,
  areArraysEqual,
  commaSeparated,
  convertToNumber,
  toIST,
  convertToMillions,
  getCurrencySymbol,
  sendMessageToCanvas,
  opacity2hex,
  roundOff,
  blobToBinary,
  getVersion,
  wrapWithAll,
  generateOptions,
  sanitizeParams,
  getCurrencyBasedData,
  getCurrencyBasedMarketData,
  getCountryCodeByName,
  getUserTimezone,
  serializeDateValue,
  calculateOverallRTP,
  navigateFromRow,
};
